import classNames from "classnames";
import ModalPortal from "components/app/_common/ModalPortal";
import { format } from "date-fns";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, CalendarProps } from "react-date-range";
import { ICalendarDot, ICNRRecord } from "redux/app/vdmdashboard/@types";
import { vdmDashboardUtils } from "redux/app/vdmdashboard/utils";
import CNRIdSelectionModalContent from "./_elements/CNRIdSelectionModalContent";

const { toUTCMidnight, toLocalMidnight } = vdmDashboardUtils;

const isValidDate = (d: any) => d instanceof Date && !isNaN(d.getTime());

type OwnProps = {
	id: string;
	handleOpen: () => void;
	onClose: () => void;
	closeOnSelection: () => void;
	isOpen: boolean;
	handleOnChange: (date: string) => void;
	onShownDateChange?: (date: Date) => void;
	selectedDate: string;
	className?: string;
	cnrDataForDateRange?: ICNRRecord[];
	isCNRForDateRangeLoading?: boolean;
	selectedCNRId: string;
	setSelectedCNRId: (cnrId: string) => void;
	loadVDMDashboardContent: (cnrId: string) => void;
	isLoading: boolean;
};
type DatePickerProps = OwnProps;
function DatePicker(props: DatePickerProps) {
	const {
		selectedDate,
		handleOnChange,
		handleOpen,
		isOpen,
		onClose,
		closeOnSelection,
		id,
		className,
		onShownDateChange,
		cnrDataForDateRange,
		isCNRForDateRangeLoading,
		selectedCNRId,
		setSelectedCNRId,
		loadVDMDashboardContent,
		isLoading,
	} = props;
	const options: Intl.DateTimeFormatOptions = {
		month: "short",
		day: "numeric",
		year: "numeric",
	};

	const dotsData = useMemo(() => {
		const data = {} as Record<string, ICalendarDot>;
		if (!cnrDataForDateRange || cnrDataForDateRange.length === 0) {
			return data;
		}
		cnrDataForDateRange?.forEach((cnr) => {
			const selectedDate = cnr?.selectedDate;
			if (selectedDate in data) {
				data[selectedDate]?.cnrDetails.push({
					id: cnr?._id,
					intlDateLineRecord: cnr?.intlDateLineRecord,
				});
			} else {
				data[selectedDate] = {
					cnrDetails: [
						{ id: cnr?._id, intlDateLineRecord: cnr?.intlDateLineRecord },
					],
				};
			}
		});
		return data;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cnrDataForDateRange]);

	const [showCnrModal, setShowCnrModal] = useState(false);
	const [modalDate, setModalDate] = useState<string | null>("");
	const [modalCNRId, setModalCNRId] = useState<string>(selectedCNRId);
	const [availableCnrs, setAvailableCnrs] = useState<
		{ id: string; intlDateLineRecord: boolean }[]
	>([]);

	const renderDayContent = (day: Date) => {
		const formattedDate = format(day, "yyyy-MM-dd");
		const key = formattedDate + "T00:00:00.000Z";
		const dots = dotsData[key] || [];

		return (
			<div className="day-cell flex flex-col items-center justify-center w-full h-full gap-0.5">
				<span className="day-number text-sm leading-4">{format(day, "d")}</span>
				{dots?.cnrDetails?.length > 0 && (
					<div className="dot-row flex gap-[3px] justify-center">
						{dots?.cnrDetails?.map((cnr, i) => {
							const { id, intlDateLineRecord } = cnr;
							return (
								<span
									key={id}
									className={classNames(`dot w-[5px] h-[5px] rounded-full`, {
										"bg-red-500": !intlDateLineRecord,
										"bg-green-400": intlDateLineRecord,
									})}
								/>
							);
						})}
					</div>
				)}
			</div>
		);
	};

	const renderNavigator = (
		currentFocusedDate: Date,
		changeShownDate: (
			value: string | number | Date,
			mode?: "monthOffset" | "set" | "setYear" | "setMonth",
		) => void,
		props: CalendarProps,
	) => {
		const btnBase =
			"flex items-center justify-center w-7 h-7 rounded transition-colors";
		const btnActive = "hover:bg-gray-100 text-gray-700 cursor-pointer";
		const btnDisabled = "opacity-30 cursor-not-allowed text-gray-400";

		return (
			<div className="flex flex-col w-full">
				<div className="flex justify-end px-2 pt-1">
					<button
						type="button"
						onClick={onClose}
						className={`${btnBase} hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors`}
						aria-label="Close"
					>
						<span className="bp3-icon bp3-icon-cross text-base" />
					</button>
				</div>
				<div className="flex items-center justify-between px-3 py-2 w-full">
					{/* Prev month */}
					<button
						type="button"
						disabled={isCNRForDateRangeLoading}
						onClick={() =>
							!isCNRForDateRangeLoading && changeShownDate(-1, "monthOffset")
						}
						className={`${btnBase} ${isCNRForDateRangeLoading ? btnDisabled : btnActive}`}
						aria-label="Previous month"
					>
						<span className="bp3-icon bp3-icon-chevron-left text-base" />
					</button>

					{/* Month + Year label */}
					<div className="text-sm font-semibold text-gray-800 select-none flex items-center gap-x-2">
						<span>{format(currentFocusedDate, "MMMM yyyy")}</span>

						{isCNRForDateRangeLoading ? (
							<div className="w-5 h-5 rounded-full border-2 border-blue-200 border-t-gray-700 animate-spin" />
						) : null}
					</div>

					{/* Next month */}
					<button
						type="button"
						disabled={isCNRForDateRangeLoading}
						onClick={() =>
							!isCNRForDateRangeLoading && changeShownDate(1, "monthOffset")
						}
						className={`${btnBase} ${isCNRForDateRangeLoading ? btnDisabled : btnActive}`}
						aria-label="Next month"
					>
						<span className="bp3-icon bp3-icon-chevron-right text-base" />
					</button>
				</div>
			</div>
		);
	};

	const [calendarKey, setCalendarKey] = useState(0);
	const hasSelectedDuringSession = useRef(false);
	useEffect(() => {
		if (isOpen) {
			hasSelectedDuringSession.current = false;
			// Force Calendar remount so it resets to shownDate, not its stale internal state
			setCalendarKey((k) => k + 1);
		}
		if (!isOpen) {
			setModalCNRId("");
		}
	}, [isOpen]);

	const initialShownDate = useMemo(() => {
		return selectedDate ? toLocalMidnight(selectedDate) : new Date();
	}, [selectedDate]);
	const calendarDate = useMemo(
		() => (selectedDate ? toLocalMidnight(selectedDate) : new Date()),
		[selectedDate],
	);

	// Handle CNR selection in modal
	const handleCnrSelection = (cnrId: string, intlDateLineRecord: boolean) => {
		if (modalDate) {
			setModalCNRId(cnrId);
			// setSelectedCNRId(cnrId);
		}
	};

	return (
		<>
			<div className={classNames("relative w-full")}>
				<div
					onClick={() => handleOpen()}
					className={classNames(
						"p-1.5 py-2 shadow rounded-md text-sm text-gray-700 flex items-center justify-between cursor-pointer",
						{ "bg-gray-200": isLoading },
						className,
					)}
				>
					<div className="flex items-center">
						<span className="bp3-icon bp3-icon-calendar text-gray-700 mr-2 " />
						<span className="mr-6 font-medium">
							{selectedDate
								? toLocalMidnight(selectedDate)?.toLocaleDateString(
										"en-US",
										options,
									)
								: "Please select"}
						</span>
					</div>
					<span className="bp3-icon bp3-icon-chevron-down text-gray-700 font-medium" />
				</div>

				<ModalPortal
					width={332}
					isActive={isOpen}
					onClose={() => {
						if (isCNRForDateRangeLoading) return;
						onClose();
					}}
				>
					<div className="flex justify-center rounded">
						{isValidDate(calendarDate) && isValidDate(initialShownDate) && (
							<Calendar
								dateDisplayFormat="dd-MM-yyyy"
								className="border shadow-xl rounded feedback"
								onChange={(date: Date) => {
									if (isCNRForDateRangeLoading) {
										return;
									}

									hasSelectedDuringSession.current = true;
									const key = toUTCMidnight(date).toISOString();

									const dots = dotsData[key] || [];
									if (isCNRForDateRangeLoading) {
										return;
									}
									if (dots.cnrDetails?.length > 1) {
										// show Modal to select cnrId.
										setModalDate(key);
										setAvailableCnrs(dots.cnrDetails);
										setShowCnrModal(true);
									} else if (dots.cnrDetails?.length === 1) {
										// Directly select date and set cnr id
										const cnrId = dots.cnrDetails[0].id;
										handleOnChange(key);
										setSelectedCNRId(cnrId);
										loadVDMDashboardContent(cnrId);
										closeOnSelection();
									}

									// handleOnChange(date);
									// // INFO: Below is the case when user selects the left side predefined options
									// onClose();
								}}
								onShownDateChange={onShownDateChange && onShownDateChange}
								shownDate={initialShownDate}
								date={calendarDate}
								key={`${id}-${calendarKey}`}
								dayContentRenderer={renderDayContent}
								navigatorRenderer={renderNavigator}
							/>
						)}
					</div>
				</ModalPortal>

				{showCnrModal && (
					<CNRIdSelectionModalContent
						handleCnrSelection={(cnrId, intlDateline) => {
							handleCnrSelection(cnrId, intlDateline);
						}}
						isOpen={isOpen}
						selectedCNRId={selectedCNRId}
						setModalCNRId={(cnrId: string) => setModalCNRId(cnrId)}
						modalCNRId={modalCNRId}
						modalDate={modalDate ?? ""}
						onClose={() => onClose()}
						closeCNRModal={() => setShowCnrModal(false)}
						onSelect={() => {
							setSelectedCNRId(modalCNRId);
							loadVDMDashboardContent(modalCNRId);
							modalDate && handleOnChange(modalDate);
							setShowCnrModal(false);
						}}
						availableCnrs={availableCnrs}
					/>
				)}
			</div>
		</>
	);
}

export default DatePicker;
